/**
 * TalentLens AI - Match Page
 * Find and compare candidates for a specific job
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import api from '../lib/api';
import { useAuthStore, canExport } from '../store/auth';

export default function MatchPage() {
  const params = useParams({ strict: false }) as { jobId: string };
  const jobId = params.jobId;
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [minScore, setMinScore] = useState(0);
  const [includeExplanation, setIncludeExplanation] = useState(true);
  const [selectedMatches, setSelectedMatches] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [addingToPipeline, setAddingToPipeline] = useState<string | null>(null);

  const { data: jobData, isLoading: jobLoading, error: jobError } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.getJob(jobId),
  });

  const { data: matchData, isLoading: matchLoading, error: matchError, refetch, isFetching } = useQuery({
    queryKey: ['matches', jobId, minScore, includeExplanation],
    queryFn: () => api.getJobMatches(jobId, {
      limit: '50',
      minScore: minScore.toString(),
      includeExplanation: includeExplanation.toString(),
    }),
    enabled: !!jobId,
  });

  const job = jobData?.data;
  const matches = matchData?.data?.matches || [];
  const totalProcessedCVs = matchData?.data?.totalProcessedCVs;

  // Pipeline data
  const { data: pipelineData } = useQuery({
    queryKey: ['pipeline', jobId],
    queryFn: () => api.getPipelineForJob(jobId),
    enabled: !!jobId,
  });

  const pipelineCvIds = new Set((pipelineData?.data || []).map((p: any) => p.cvId));

  const addToPipelineMutation = useMutation({
    mutationFn: (cvId: string) => api.addToPipeline({ job_id: jobId, cv_id: cvId }),
    onMutate: (cvId) => setAddingToPipeline(cvId),
    onSettled: () => setAddingToPipeline(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline', jobId] });
    },
  });

  const addSelectedToPipeline = async () => {
    const cvIds = Array.from(selectedMatches);
    for (const cvId of cvIds) {
      if (!pipelineCvIds.has(cvId)) {
        try {
          await api.addToPipeline({ job_id: jobId, cv_id: cvId });
        } catch (e) {
          // Already in pipeline, skip
        }
      }
    }
    queryClient.invalidateQueries({ queryKey: ['pipeline', jobId] });
    setSelectedMatches(new Set());
  };

  const toggleSelectMatch = (cvId: string) => {
    const newSelected = new Set(selectedMatches);
    if (newSelected.has(cvId)) {
      newSelected.delete(cvId);
    } else {
      newSelected.add(cvId);
    }
    setSelectedMatches(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedMatches.size === matches.length) {
      setSelectedMatches(new Set());
    } else {
      setSelectedMatches(new Set(matches.map((m: any) => m.cvId)));
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const matchesToExport = selectedMatches.size > 0
        ? matches.filter((m: any) => selectedMatches.has(m.cvId))
        : matches;
      const csv = await api.exportMatchesCSV(matchesToExport, job?.title);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `matches-${job?.title?.replace(/\s+/g, '-') || 'job'}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  if (jobLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 h-96" />
      </div>
    );
  }

  // Error state for job loading
  if (jobError) {
    return (
      <div className="text-center py-12">
        <ErrorIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Failed to Load Job</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {(jobError as any)?.message || 'Unable to load job details. Please try again.'}
        </p>
        <Link
          to="/jobs"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          &larr; Back to Jobs
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link to="/jobs" className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2 inline-block">
            &larr; Back to Jobs
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Candidate Matching</h1>
          {job && (
            <p className="text-gray-600 dark:text-gray-400">
              Finding candidates for: <span className="font-medium text-gray-900 dark:text-white">{job.title}</span>
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/pipeline/$jobId"
            params={{ jobId }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PipelineIcon className="w-4 h-4" />
            View Pipeline
            {pipelineCvIds.size > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-primary-500 rounded-full">{pipelineCvIds.size}</span>
            )}
          </Link>

          {canExport(user) && matches.length > 0 && (
            <button
              onClick={handleExportCSV}
              disabled={isExporting}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              <ExportIcon className="w-4 h-4" />
              Export {selectedMatches.size > 0 ? `(${selectedMatches.size})` : 'All'}
            </button>
          )}
        </div>
      </div>

      {/* Error display */}
      {matchError && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ErrorIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900 dark:text-red-300">Error Loading Matches</h3>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                {(matchError as any)?.message || 'Failed to load matching candidates. Please try again.'}
              </p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Score Calculation Info */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 dark:text-blue-200">How Match Scores Work</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Scores are calculated using: <strong>60% Vector Similarity</strong> (semantic match),
              <strong> 20% Skills Match</strong> (exact + fuzzy matching), and
              <strong> 20% Experience Relevance</strong> (years and seniority level).
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-700 dark:text-gray-300">Minimum Score:</label>
            <input
              type="range"
              min="0"
              max="80"
              step="10"
              value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))}
              className="w-32 accent-primary-600"
            />
            <span className="text-sm font-medium text-gray-900 dark:text-white w-8">{minScore}%</span>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeExplanation}
              onChange={(e) => setIncludeExplanation(e.target.checked)}
              className="rounded text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Include AI Explanations</span>
          </label>

          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isFetching && <LoadingSpinner className="w-4 h-4" />}
            {isFetching ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Batch actions */}
      {selectedMatches.size > 0 && (
        <div className="flex items-center gap-4 px-4 py-3 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
            {selectedMatches.size} selected
          </span>
          <button
            onClick={addSelectedToPipeline}
            className="px-3 py-1.5 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded-lg"
          >
            Add to Pipeline
          </button>
          <button
            onClick={handleExportCSV}
            disabled={isExporting}
            className="px-3 py-1.5 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-lg"
          >
            Export Selected
          </button>
          <button
            onClick={() => setSelectedMatches(new Set())}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Select all */}
      {matches.length > 0 && (
        <div className="flex items-center gap-2 px-2">
          <input
            type="checkbox"
            checked={selectedMatches.size === matches.length && matches.length > 0}
            onChange={toggleSelectAll}
            className="rounded text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">Select all {matches.length} matches</span>
        </div>
      )}

      {/* Results */}
      {matchLoading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 animate-pulse h-32" />
          ))}
        </div>
      ) : matches.length === 0 ? (
        <EmptyMatchesState
          minScore={minScore}
          totalProcessedCVs={totalProcessedCVs}
          onLowerScore={() => setMinScore(0)}
        />
      ) : (
        <div className="grid gap-4">
          {matches.map((match: any, index: number) => (
            <div
              key={match.cvId}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow ${
                selectedMatches.has(match.cvId) ? 'ring-2 ring-primary-500' : ''
              }`}
            >
              <div className="flex items-start gap-6">
                {/* Checkbox */}
                <div className="flex-shrink-0 pt-3">
                  <input
                    type="checkbox"
                    checked={selectedMatches.has(match.cvId)}
                    onChange={() => toggleSelectMatch(match.cvId)}
                    className="rounded text-primary-600 focus:ring-primary-500"
                  />
                </div>

                {/* Rank */}
                <div className="flex-shrink-0 w-12 h-12 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-primary-700 dark:text-primary-300">#{index + 1}</span>
                </div>

                {/* Candidate Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <Link
                      to="/cvs/$cvId"
                      params={{ cvId: match.cvId }}
                      className="font-semibold text-lg text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400"
                    >
                      {match.candidate?.name || 'Unknown'}
                    </Link>
                    {pipelineCvIds.has(match.cvId) ? (
                      <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                        In Pipeline
                      </span>
                    ) : (
                      <button
                        onClick={() => addToPipelineMutation.mutate(match.cvId)}
                        disabled={addingToPipeline === match.cvId}
                        className="px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded hover:bg-primary-200 dark:hover:bg-primary-900/50 disabled:opacity-50"
                      >
                        {addingToPipeline === match.cvId ? 'Adding...' : '+ Add to Pipeline'}
                      </button>
                    )}
                  </div>

                  {match.candidate?.email && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{match.candidate.email}</p>
                  )}

                  {/* Skills with match indicator */}
                  {match.candidate?.skills && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {match.candidate.skills.slice(0, 8).map((skill: any, i: number) => {
                        const isMatched = match.explanation?.skills_match?.matched?.includes(skill.skill);
                        return (
                          <span
                            key={i}
                            className={`px-2 py-1 rounded text-xs ${
                              isMatched
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {skill.skill} ({skill.years}y)
                            {isMatched && <CheckIcon className="w-3 h-3 inline ml-1" />}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Explanation */}
                  {match.explanation && (
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">AI Analysis</h4>

                      {/* Score breakdown */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="text-center p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                            {match.explanation.skills_match?.score || 0}%
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Skills Match</p>
                          {match.explanation.skills_match?.matched?.length > 0 && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              {match.explanation.skills_match.matched.length} matched
                            </p>
                          )}
                        </div>
                        <div className="text-center p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                            {match.explanation.experience_match?.score || 0}%
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Experience</p>
                          {match.explanation.experience_match?.summary && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                              {match.explanation.experience_match.summary}
                            </p>
                          )}
                        </div>
                        <div className="text-center p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                            {match.explanation.education_match?.score || 0}%
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Education</p>
                        </div>
                      </div>

                      {match.explanation.highlights && (
                        <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                          {match.explanation.highlights.map((h: string, i: number) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {h}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Missing skills */}
                      {match.explanation.skills_match?.missing?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Missing skills:</p>
                          <div className="flex flex-wrap gap-1">
                            {match.explanation.skills_match.missing.map((skill: string, i: number) => (
                              <span key={i} className="px-2 py-0.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Score */}
                <div className="flex-shrink-0 text-center">
                  <div className="relative group">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center cursor-help ${getScoreColor(match.score)}`}>
                      <span className="text-2xl font-bold">{match.score}</span>
                    </div>

                    {/* Score breakdown tooltip */}
                    <div className="absolute bottom-full right-0 mb-2 w-48 p-3 bg-gray-900 text-white rounded-lg text-xs hidden group-hover:block z-10">
                      <p className="font-medium mb-2">Score Breakdown</p>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>Vector (60%)</span>
                          <span>{Math.round((match.vectorSimilarity || 0))}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Skills (20%)</span>
                          <span>{match.skillsScore || 0}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Experience (20%)</span>
                          <span>{match.experienceScore || 0}%</span>
                        </div>
                      </div>
                      <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Match Score</p>

                  {/* Quick score breakdown */}
                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span>Vec: {Math.round((match.vectorSimilarity || 0))}%</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span>Skill: {match.skillsScore || 0}%</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-purple-500" />
                      <span>Exp: {match.experienceScore || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
  if (score >= 60) return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300';
  if (score >= 40) return 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300';
  return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
}

// Empty Matches State Component
function EmptyMatchesState({
  minScore,
  totalProcessedCVs,
  onLowerScore,
}: {
  minScore: number;
  totalProcessedCVs?: number;
  onLowerScore: () => void;
}) {
  const hasProcessedCVs = totalProcessedCVs !== undefined && totalProcessedCVs > 0;
  const isFiltered = minScore > 0;

  return (
    <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
      <UsersIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        {isFiltered ? 'No candidates meet the criteria' : 'No matching candidates found'}
      </h3>

      {!hasProcessedCVs ? (
        <div className="max-w-md mx-auto">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            There are no processed CVs in the system yet. Upload CVs and wait for them to be processed before matching.
          </p>
          <div className="flex justify-center gap-3">
            <Link
              to="/cvs"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <UploadIcon className="w-4 h-4" />
              Upload CVs
            </Link>
          </div>
        </div>
      ) : isFiltered ? (
        <div className="max-w-md mx-auto">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            No candidates have a match score of {minScore}% or higher for this job.
          </p>
          <button
            onClick={onLowerScore}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Show all candidates
          </button>
        </div>
      ) : (
        <div className="max-w-md mx-auto">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            No candidates in your CV pool match the requirements for this job. This could happen if:
          </p>
          <ul className="text-sm text-gray-500 dark:text-gray-400 text-left list-disc pl-6 mb-4 space-y-1">
            <li>The job requirements are very specific</li>
            <li>CVs haven't finished processing yet</li>
            <li>There are issues with the CV embeddings</li>
          </ul>
          <div className="flex justify-center gap-3">
            <Link
              to="/cvs"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              View CVs
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// Loading Spinner Component
function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className || 'w-5 h-5'}`} viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ExportIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  );
}

function PipelineIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
    </svg>
  );
}
